const DEFAULT_NUMBER = 0;
const DEFAULT_CURRENCY = 'ru-RU';
const DEFAULT_TO_FIXED_NUMBER = 2;

/**
 * Форматирует число и приводит к ценовому формату, например 200 000,00.
 *
 * @param number — число, которое необходимо отформатировать
 * @param currency — локаль форматирования (по умолчанию ru-RU)
 * @param toFixedNumber — количество цифр после запятой (по умолчанию 2)
 * @param numberFormatOptions — доп. опции Intl.NumberFormat
 * @returns отформатированное число
 */
export const numberFormatter = (
  number: number = DEFAULT_NUMBER,
  currency: string = DEFAULT_CURRENCY,
  toFixedNumber: number = DEFAULT_TO_FIXED_NUMBER,
  numberFormatOptions: Intl.NumberFormatOptions = {},
): string => new Intl.NumberFormat(currency, numberFormatOptions).format(Number(number.toFixed(toFixedNumber)));

/**
 * Форматирует число, всегда отображая ровно toFixedNumber знаков после запятой
 * (в т.ч. нули, например 413,0). В отличие от {@link numberFormatter}, не отбрасывает
 * незначащие нули дробной части.
 *
 * @param number — число для форматирования
 * @param currency — локаль форматирования (по умолчанию ru-RU)
 * @param toFixedNumber — количество знаков после запятой (по умолчанию 2)
 * @returns отформатированное число, например 1 234,0
 */
export const numberFormatterWithFixedFractionDigits = (
  number: number = DEFAULT_NUMBER,
  currency: string = DEFAULT_CURRENCY,
  toFixedNumber: number = DEFAULT_TO_FIXED_NUMBER,
): string =>
  numberFormatter(number, currency, toFixedNumber, {
    minimumFractionDigits: toFixedNumber,
    maximumFractionDigits: toFixedNumber,
  });

/**
 * Форматирует число, разделяя пробелами только целую часть.
 * Дробная часть сохраняется как в исходном значении (без принудительного округления)
 * и ставится через запятую.
 *
 * @param value — число для форматирования
 * @param currency — локаль форматирования (по умолчанию ru-RU)
 * @returns отформатированное число, например 1 234,56
 */
export const numberFormatterKeepDecimals = (
  value: number | string | null,
  currency: string = DEFAULT_CURRENCY,
): string => {
  if (!Number.isFinite(value)) return '';
  const [intPart, decPart] = String(value).split(/[.,]/);
  const formattedInt = numberFormatter(Number(intPart), currency, 0);
  return decPart ? `${formattedInt},${decPart}` : formattedInt;
};
